/**
 * CPT code reference data for autocomplete in the setup wizard.
 *
 * Sourced from the global CPT code seed (db/seeds/seed_47_cpt_codes.py).
 * This enables type-ahead suggestions without requiring an API call.
 */

import type { CptCodeEntry } from '../types';

export interface CPTCodeOption {
  code: string;
  description: string;
  /** True for codes that must be billed alongside a primary service (e.g. 90833, 90836). */
  isAddOn?: boolean;
}

/**
 * Complete list of mental-health CPT codes seeded in the database.
 * Each entry mirrors a row from seed_47_cpt_codes.py.
 *
 * Codes marked `isAddOn: true` are add-on codes that cannot be billed
 * standalone — they must accompany a primary service code.
 */
export const CPT_CODE_OPTIONS: CPTCodeOption[] = [
  // Evaluation and Management - New Patient
  { code: '99202', description: 'Office/outpatient visit, new patient, 15-29 minutes' },
  { code: '99203', description: 'Office/outpatient visit, new patient, 30-44 minutes' },
  { code: '99204', description: 'Office/outpatient visit, new patient, 45-59 minutes' },
  { code: '99205', description: 'Office/outpatient visit, new patient, 60-74 minutes' },
  // Evaluation and Management - Established Patient
  { code: '99211', description: 'Office/outpatient visit, established patient, may not require physician' },
  { code: '99212', description: 'Office/outpatient visit, established patient, 10-19 minutes' },
  { code: '99213', description: 'Office/outpatient visit, established patient, 20-29 minutes' },
  { code: '99214', description: 'Office/outpatient visit, established patient, 30-39 minutes' },
  { code: '99215', description: 'Office/outpatient visit, established patient, 40-54 minutes' },
  // Psychiatric Evaluations
  { code: '90791', description: 'Psychiatric diagnostic evaluation' },
  { code: '90792', description: 'Psychiatric diagnostic evaluation with medical services' },
  // Psychotherapy - Individual
  { code: '90832', description: 'Psychotherapy, 30 minutes with patient (16-37 minutes)' },
  { code: '90834', description: 'Psychotherapy, 45 minutes with patient (38-52 minutes)' },
  { code: '90837', description: 'Psychotherapy, 60 minutes with patient (53+ minutes)' },
  // Psychotherapy - Crisis
  { code: '90839', description: 'Psychotherapy for crisis, first 60 minutes (30-74 minutes)' },
  { code: '90840', description: 'Psychotherapy for crisis, each additional 30 minutes (add-on)', isAddOn: true },
  // Psychotherapy Add-on Codes
  { code: '90785', description: 'Interactive complexity (add-on to primary service)', isAddOn: true },
  { code: '90833', description: 'Psychotherapy, 30 minutes with E/M service (add-on, 16-37 min)', isAddOn: true },
  { code: '90836', description: 'Psychotherapy, 45 minutes with E/M service (add-on, 38-52 min)', isAddOn: true },
  { code: '90838', description: 'Psychotherapy, 60 minutes with E/M service (add-on, 53+ min)', isAddOn: true },
  // Psychoanalysis & Other Psychiatry
  { code: '90845', description: 'Psychoanalysis' },
  { code: '90875', description: 'Individual psychophysiological therapy with biofeedback, 30 minutes' },
  { code: '90876', description: 'Individual psychophysiological therapy with biofeedback, 45 minutes' },
  { code: '90880', description: 'Hypnotherapy' },
  // Unlisted Psychiatric Service
  { code: '90899', description: 'Unlisted psychiatric service' },
  // Medication Management
  { code: '90863', description: 'Pharmacologic management, including prescription and review of medication' },
  // Family / Couples Therapy
  { code: '90846', description: 'Family psychotherapy without patient present, 50 minutes' },
  { code: '90847', description: 'Family psychotherapy with patient present, 50 minutes' },
  { code: '90887', description: 'Interpretation/explanation of results to family, 50 minutes' },
  { code: '90849', description: 'Multiple-family group psychotherapy' },
  // Group Therapy
  { code: '90853', description: 'Group psychotherapy (other than multiple-family group)' },
  // Psychological Testing
  { code: '96130', description: 'Psychological testing evaluation services, first hour' },
  { code: '96131', description: 'Psychological testing evaluation, each additional hour (add-on)', isAddOn: true },
  { code: '96132', description: 'Neuropsychological testing evaluation services, first hour' },
  { code: '96133', description: 'Neuropsychological testing evaluation, each additional hour (add-on)', isAddOn: true },
  { code: '96136', description: 'Psychological/neuropsychological test administration by professional, first 30 minutes' },
  { code: '96137', description: 'Psychological/neuropsychological test administration by professional, each additional 30 minutes (add-on)', isAddOn: true },
  { code: '96138', description: 'Psychological/neuropsychological test administration by technician, first 30 minutes' },
  { code: '96139', description: 'Psychological/neuropsychological test administration by technician, each additional 30 minutes (add-on)', isAddOn: true },
  { code: '96116', description: 'Neurobehavioral status exam, first hour' },
  { code: '96121', description: 'Neurobehavioral status exam, each additional hour (add-on)', isAddOn: true },
  { code: '96127', description: 'Brief emotional/behavioral assessment (screening)' },
  // Developmental Testing
  { code: '96112', description: 'Developmental test administration, first hour' },
  { code: '96113', description: 'Developmental test administration, each additional 30 minutes (add-on)', isAddOn: true },
  // Automated Testing
  { code: '96146', description: 'Psychological or neuropsychological test administration, automated' },
  // Biofeedback
  { code: '90901', description: 'Biofeedback training by any modality' },
  // Interprofessional Consultation
  { code: '99446', description: 'Interprofessional consultation, 5-10 minutes' },
  { code: '99447', description: 'Interprofessional consultation, 11-20 minutes' },
  { code: '99448', description: 'Interprofessional consultation, 21-30 minutes' },
  { code: '99449', description: 'Interprofessional consultation, 31+ minutes' },
  { code: '99451', description: 'Interprofessional consultation via written report only' },
];

/** CPT code → unit time in minutes (from seed_47_cpt_codes.py). Used for auto-calculating units. */
export const CPT_UNIT_TIMES: Record<string, number> = {
  '99202': 20, '99203': 30, '99204': 45, '99205': 60,
  '99211': 10, '99212': 15, '99213': 20, '99214': 30, '99215': 40,
  '90791': 60, '90792': 60,
  '90832': 30, '90834': 45, '90837': 60,
  '90839': 60, '90840': 30,
  '90785': 0, '90833': 30, '90836': 45, '90838': 60,
  '90845': 60, '90875': 30, '90876': 45, '90880': 60,
  '90899': 60, '90863': 15,
  '90846': 50, '90847': 50, '90849': 50, '90853': 50,
  '96130': 60, '96131': 60, '96132': 60, '96133': 60,
  '96136': 30, '96137': 30, '96138': 30, '96139': 30,
  '96116': 60, '96121': 60, '96127': 15,
  '96112': 60, '96113': 30,
  '96146': 30, '90901': 45,
  '99446': 10, '99447': 20, '99448': 30, '99449': 40, '99451': 30,
};

/**
 * Base CPT code → add-on CPT code pairing for first-unit/additional-unit patterns.
 * Used to auto-calculate add-on units from appointment duration.
 * E.g., 96136 (first 30 min) → 96137 (each additional 30 min).
 */
export const CPT_ADDON_PAIRS: Record<string, string> = {
  '96136': '96137',  // professional test admin: first 30 → additional 30
  '96138': '96139',  // technician test admin: first 30 → additional 30
  '96130': '96131',  // psych testing eval: first 60 → additional 60
  '96132': '96133',  // neuropsych testing eval: first 60 → additional 60
  '96116': '96121',  // neurobehavioral status: first 60 → additional 60
  '96112': '96113',  // developmental test admin: first 60 → additional 30
  '90839': '90840',  // crisis psychotherapy: first 60 → additional 30
};

/** Convenience subsets for UI filtering. */
export const STANDALONE_CPT_OPTIONS = CPT_CODE_OPTIONS.filter((o) => !o.isAddOn);
export const ADDON_CPT_OPTIONS = CPT_CODE_OPTIONS.filter((o) => o.isAddOn);

/** Set of add-on code strings for quick membership checks. */
export const ADDON_CODE_SET = new Set(ADDON_CPT_OPTIONS.map((o) => o.code));

/** Breakdown of units for a paired CPT code (primary + add-on). */
export interface PairedCptBreakdown {
  primaryCode: string;
  primaryUnits: number;
  addonCode: string | null;
  addonUnits: number;
}

/**
 * For paired CPT codes (e.g. 96130/96131), calculate the unit breakdown
 * for a given duration. The primary code always gets 1 unit (covers the
 * first period). The add-on code is billed only when the duration exceeds
 * the primary period by more than half of an additional unit interval. This
 * mirrors the CPT "more than halfway" time threshold commonly applied to
 * timed add-on codes (e.g. 96131 requires at least 31 additional minutes).
 *
 * Returns null if the code has no paired add-on.
 */
export function calculatePairedCptBreakdown(
  duration: number,
  primaryCode: string,
): PairedCptBreakdown | null {
  const addonCode = CPT_ADDON_PAIRS[primaryCode];
  if (!addonCode) return null;

  const primaryUnitTime = CPT_UNIT_TIMES[primaryCode];
  const addonUnitTime = CPT_UNIT_TIMES[addonCode];
  if (!primaryUnitTime || !addonUnitTime) return null;

  const remaining = duration - primaryUnitTime;
  // Require at least half the add-on interval plus one minute before billing
  // an additional unit.  For a 60-minute add-on interval, 31 minutes beyond
  // the first hour yields 1 add-on unit; 30 minutes yields 0.
  const addonUnits =
    remaining > 0
      ? Math.max(0, Math.ceil((remaining - addonUnitTime / 2) / addonUnitTime))
      : 0;

  return {
    primaryCode,
    primaryUnits: 1,
    addonCode,
    addonUnits,
  };
}

/**
 * Build per-duration add-on code entries for a paired CPT code.
 * E.g. 96130 (first hour) → [{ code: '96131', units: N }] based on duration.
 * Returns an empty array when no paired add-on exists or units would be 0.
 */
export function buildPairedAddonCodes(
  duration: number,
  cptCode: string,
): CptCodeEntry[] {
  if (!cptCode) return [];
  const breakdown = calculatePairedCptBreakdown(duration, cptCode);
  if (!breakdown?.addonCode || breakdown.addonUnits <= 0) return [];
  return [{ code: breakdown.addonCode, units: breakdown.addonUnits }];
}

/** Filter CPT code options based on user input (code or description substring match). */
export function filterCptOptions(options: CPTCodeOption[], inputValue: string): CPTCodeOption[] {
  const q = inputValue.toLowerCase();
  if (!q) return options;
  return options.filter(
    (o) => o.code.includes(q) || o.description.toLowerCase().includes(q),
  );
}

/**
 * Suggest an appropriate *primary* CPT code based on appointment type name,
 * category, and duration. Mirrors the default_cpt assignments in
 * db/seeds/seed_45_appointment_types.py so that every billable row starts
 * with a sensible value the user can override.
 *
 * Only *clinical* categories receive a CPT suggestion; non-clinical and admin
 * categories return an empty string.
 */
export function suggestCptCode(
  name: string,
  category: string,
  durationMinutes: number,
): string {
  const n = name.toLowerCase();
  const cat = category.toLowerCase();

  // Only clinical categories get CPT codes — non-clinical / admin / unknown stay blank
  const CLINICAL_CATS = new Set([
    'therapy_individual', 'therapy_couples_family',
    'psychiatry', 'assessment', 'kap', 'group_therapy', 'neurofeedback',
    'crisis', 'intensives',
  ]);
  if (!CLINICAL_CATS.has(cat)) return '';

  switch (cat) {
    case 'therapy_individual':
    case 'therapy_couples_family': {
      // Couples / family variants always use 90847
      if (n.includes('couples') || n.includes('family')) return '90847';
      // Intakes use diagnostic evaluation
      if (n.includes('intake')) return '90791';
      // Crisis intervention
      if (n.includes('crisis')) return '90839';
      // Duration-based psychotherapy codes
      if (durationMinutes <= 37) return '90832';
      if (durationMinutes <= 52) return '90834';
      return '90837';
    }
    case 'psychiatry': {
      if (n.includes('intake')) return '90792';
      if (n.includes('consultation')) return '99448';
      // Follow-up / medication management — primary psychotherapy code by duration
      if (durationMinutes <= 37) return '90832';
      if (durationMinutes <= 52) return '90834';
      return '90837';
    }
    case 'assessment': {
      // Intake / initial interview
      if (n.includes('intake') || n.includes('initial') || n.includes('interview')) return '90791';
      // Records review uses test-administration 30-min increments (96136 + 96137)
      if (n.includes('records review')) return '96136';
      // Automated test administration (e.g., via electronic platform) before generic admin
      if (n.includes('automat')) return '96146';
      // Test administration by technician or professional. Check before generic
      // 'test'/'eval' so "Test Administration" → 96136/96138, not 96130.
      if (
        n.includes('admin') &&
        !n.includes('eval') && !n.includes('interpretation') && !n.includes('report')
      )
        return n.includes('tech') ? '96138' : '96136';
      // Scoring, interpretation, report writing, or general "testing" evaluation → 96130
      if (n.includes('scoring') || n.includes('interpretation') || n.includes('report'))
        return '96130';
      if (n.includes('test') || n.includes('eval')) return '96130';
      // Collateral contact / explanation of results to family. Keep after
      // specific test/admin/scoring terms so names like "Family Test Administration"
      // route to the testing code instead of collateral.
      if (n.includes('collateral') || n.includes('family')) return '90887';
      // Feedback session — billed as individual therapy by duration
      if (n.includes('feedback')) {
        if (durationMinutes <= 37) return '90832';
        if (durationMinutes <= 52) return '90834';
        return '90837';
      }
      // Default for assessment is intake interview
      return '90791';
    }
    case 'kap': {
      if (n.includes('intake')) return '90792';
      if (n.includes('psychiatry') && n.includes('initial')) return '90792';
      // All non-intake KAP psychiatry (follow-up, subsequent, etc.) → duration-based
      if (n.includes('psychiatry')) {
        if (durationMinutes <= 37) return '90832';
        if (durationMinutes <= 52) return '90834';
        return '90837';
      }
      return '90837';
    }
    case 'group_therapy': {
      if (n.includes('multi') || n.includes('multiple')) return '90849';
      return '90853';
    }
    case 'crisis': {
      // Base crisis code; add-on 90840 handled via complexity method
      return '90839';
    }
    case 'neurofeedback':
      return '90901';
    case 'intensives': {
      // Intensives vary widely — suggest duration-based therapy codes as starting point
      if (n.includes('intake')) return '90791';
      if (durationMinutes <= 37) return '90832';
      if (durationMinutes <= 52) return '90834';
      return '90837';
    }
    default:
      return '';
  }
}

/**
 * Suggest a full set of CPT codes (primary + add-ons) with default units
 * for an appointment type. This is the multi-code version of suggestCptCode.
 *
 * Returns an array of CptCodeEntry objects. The first entry is always the
 * primary code; subsequent entries are add-on codes.
 *
 * Examples:
 * - Psychiatry Follow Up (45 min) → [{ code: '90834', units: 1 }, { code: '90836', units: 1 }]
 * - Psychiatry Follow Up (30 min) → [{ code: '90832', units: 1 }, { code: '90833', units: 1 }]
 * - Individual Therapy (50 min) → [{ code: '90834', units: 1 }]
 * - Test Administration → [{ code: '96136', units: 1 }, { code: '96137', units: 1 }]
 * - Crisis Intervention → [{ code: '90839', units: 1 }, { code: '90840', units: 1 }]
 */
export function suggestCptCodes(
  name: string,
  category: string,
  durationMinutes: number,
): CptCodeEntry[] {
  const primary = suggestCptCode(name, category, durationMinutes);
  if (!primary) return [];

  const n = name.toLowerCase();
  const cat = category.toLowerCase();
  const entries: CptCodeEntry[] = [{ code: primary, units: 1 }];

  // Psychiatry follow-ups pair E/M with a psychotherapy add-on
  if (cat === 'psychiatry' && !n.includes('intake') && !n.includes('consultation')) {
    if (durationMinutes <= 37) {
      entries.push({ code: '90833', units: 1 });
    } else if (durationMinutes <= 52) {
      entries.push({ code: '90836', units: 1 });
    } else {
      entries.push({ code: '90838', units: 1 });
    }
  }

  // KAP psychiatry sessions (non-intake) also pair E/M with psychotherapy add-on
  if (cat === 'kap' && n.includes('psychiatry') && !n.includes('initial') && !n.includes('intake')) {
    if (durationMinutes <= 37) {
      entries.push({ code: '90833', units: 1 });
    } else if (durationMinutes <= 52) {
      entries.push({ code: '90836', units: 1 });
    } else {
      entries.push({ code: '90838', units: 1 });
    }
  }

  // Assessment: paired add-ons derive units from duration
  if (cat === 'assessment') {
    const addon = buildPairedAddonCodes(durationMinutes, primary);
    if (addon.length) entries.push(...addon);
  }

  // Crisis intervention (90839) pairs with 90840 add-on for each additional 30 min
  if (primary === '90839') {
    const addon = buildPairedAddonCodes(durationMinutes, primary);
    if (addon.length) entries.push(...addon);
  }

  return entries;
}
